import React, { useRef, useState, useEffect } from 'react';
import { Stack, DefaultButton, Text } from '@fluentui/react';
import { BroomRegular, DismissRegular, SquareRegular } from '@fluentui/react-icons';
import { SpeechRecognizer, ResultReason } from 'microsoft-cognitiveservices-speech-sdk';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { v4 as uuidv4 } from 'uuid';

import styles from './InterviewQuestions.module.css';
import { multiLingualSpeechRecognizer } from '../../utilities/SpeechToText';

import {
  ChatMessage,
  ConversationRequest,
  customConversationApi,
  Citation,
  ToolMessageContent,
  ChatResponse,
} from '../../api';
import { Answer } from '../../components/Answer';
import { QuestionInput } from '../../components/QuestionInput';
import Shortcuts from './components/Shortcuts';

const InterviewQuestions = () => {
  const lastQuestionRef = useRef<string>('');
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
  const [activeCitation, setActiveCitation] =
    useState<[content: string, id: string, title: string, filepath: string, url: string, metadata: string]>();
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false);
  const [answers, setAnswers] = useState<ChatMessage[]>([]);
  const abortFuncs = useRef([] as AbortController[]);
  const [conversationId, setConversationId] = useState<string>(uuidv4());
  const [userMessage, setUserMessage] = useState('');
  const [recognizedText, setRecognizedText] = useState<string>('');
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognizerRef = useRef<SpeechRecognizer | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setSelectedFile(file);
    } else {
      alert('Please select a file first!');
    }
  };

  const makeApiRequest = async (question: string) => {
    lastQuestionRef.current = question;

    setIsLoading(true);
    setShowLoadingMessage(true);
    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);

    const userMessage: ChatMessage = {
      role: 'user',
      content: recognizedText || question,
    };

    const request: ConversationRequest = {
      id: conversationId,
      messages: [...answers, userMessage],
    };

    let result = {} as ChatResponse;
    try {
      const response = await customConversationApi(request, selectedFile, abortController.signal);
      if (response?.body) {
        const reader = response.body.getReader();
        let runningText = '';
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const text = new TextDecoder('utf-8').decode(value);
          const objects = text.split('\n');
          objects.forEach(obj => {
            try {
              runningText += obj;
              result = JSON.parse(runningText);
              setShowLoadingMessage(false);
              if (result.error) {
                setAnswers([...answers, userMessage, { role: 'error', content: result.error }]);
              } else {
                setAnswers([...answers, userMessage, ...result.choices[0].messages]);
              }
              runningText = '';
            } catch {}
          });
        }
        setAnswers([...answers, userMessage, ...result.choices[0].messages]);
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        console.error(result);
        alert('An error occurred. Please try again. If the problem persists, please contact the site administrator.');
      }
      setAnswers([...answers, userMessage]);
    } finally {
      setIsLoading(false);
      setShowLoadingMessage(false);
      abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
    }

    return abortController.abort();
  };

  const startSpeechRecognition = async () => {
    if (!isRecognizing) {
      setIsRecognizing(true);

      recognizerRef.current = await multiLingualSpeechRecognizer(); // Store the recognizer in the ref

      recognizerRef.current.recognized = (s, e) => {
        if (e.result.reason === ResultReason.RecognizedSpeech) {
          const recognized = e.result.text;
          setUserMessage(recognized);
          setRecognizedText(recognized);
        }
      };

      recognizerRef.current.startContinuousRecognitionAsync(() => {
        setIsRecognizing(true);
        setIsListening(true);
      });
    }
  };

  const stopSpeechRecognition = () => {
    if (isRecognizing) {
      if (recognizerRef.current) {
        recognizerRef.current.stopContinuousRecognitionAsync(() => {
          recognizerRef.current?.close();
        });
      }
      setIsRecognizing(false);
      setRecognizedText('');
      setIsListening(false);
    }
  };

  const onMicrophoneClick = async () => {
    if (!isRecognizing) {
      await startSpeechRecognition();
    } else {
      stopSpeechRecognition();
      setRecognizedText(userMessage);
    }
  };

  const clearChat = () => {
    lastQuestionRef.current = '';
    setActiveCitation(undefined);
    setAnswers([]);
    setConversationId(uuidv4());
  };

  const stopGenerating = () => {
    abortFuncs.current.forEach(a => a.abort());
    setShowLoadingMessage(false);
    setIsLoading(false);
  };

  useEffect(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: 'smooth' }), [showLoadingMessage]);

  const onShowCitation = (citation: Citation) => {
    setActiveCitation([citation.content, citation.id, citation.title ?? '', citation.filepath ?? '', '', '']);
    setIsCitationPanelOpen(true);
  };

  const parseCitationFromMessage = (message: ChatMessage) => {
    if (message.role === 'tool') {
      try {
        const toolMessage = JSON.parse(message.content) as ToolMessageContent;
        return toolMessage.citations;
      } catch {
        return [];
      }
    }
    return [];
  };

  const handleShortcutPrompt = (prompt: string) => {
    makeApiRequest(prompt);
  };

  return (
    <>
      <div className={`${styles.chatContainer} ${styles.MobileChatContainer}`}>
        <div className={styles.header}>
          <div className={styles.spacer}></div>
        </div>
        {!lastQuestionRef.current ? (
          <Stack className={styles.chatEmptyState}>
            <h3 className={styles.chatEmptyStateTitle}>
              Tell us about the role you are interviewing for, either by uploading a position description, <br></br>or
              by telling us more about the role.
            </h3>
            <Shortcuts handleShortcutPrompt={handleShortcutPrompt} />
          </Stack>
        ) : (
          <div className={styles.chatMessageStream} style={{ marginBottom: isLoading ? '40px' : '0px' }}>
            {answers.map((answer, index) => (
              <React.Fragment key={index}>
                {answer.role === 'user' ? (
                  <div className={styles.chatMessageUser}>
                    <div className={styles.chatMessageUserMessage}>{answer.content}</div>
                  </div>
                ) : answer.role === 'assistant' || answer.role === 'error' ? (
                  <div className={styles.chatMessageGpt}>
                    <Answer
                      answer={{
                        answer:
                          answer.role === 'assistant'
                            ? answer.content
                            : 'Sorry, an error occurred. Try refreshing the conversation or waiting a few minutes. If the issue persists, contact your system administrator. Error: ' +
                              answer.content,
                        citations: answer.role === 'assistant' ? parseCitationFromMessage(answers[index - 1]) : [],
                      }}
                      onCitationClicked={c => onShowCitation(c)}
                      index={index}
                    />
                  </div>
                ) : null}
              </React.Fragment>
            ))}
            {showLoadingMessage && (
              <React.Fragment key="loading">
                <div className={styles.chatMessageUser}>
                  <div className={styles.chatMessageUserMessage}>{lastQuestionRef.current}</div>
                </div>
                <div className={styles.chatMessageGpt}>
                  <Answer
                    answer={{
                      answer: 'Generating answer...',
                      citations: [],
                    }}
                    onCitationClicked={() => null}
                    index={0}
                  />
                </div>
              </React.Fragment>
            )}
            <div ref={chatMessageStreamEnd} />
          </div>
        )}

        {!lastQuestionRef.current && (
          <Stack horizontalAlign="end" style={{ paddingBottom: '1rem' }}>
            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
              {selectedFile && (
                <Text variant="medium" styles={{ root: { marginRight: 10 } }}>
                  {selectedFile.name}
                </Text>
              )}
              <DefaultButton
                text="Select PD"
                onClick={() => document.getElementById('file-input')?.click()}
                iconProps={{ iconName: 'ArrowUploadRegular' }}
              />
            </Stack>
            <input type="file" id="file-input" style={{ display: 'none' }} onChange={handleFileChange} />
          </Stack>
        )}

        <div>
          {isRecognizing && !isListening && <p>Please wait...</p>} {isListening && <p>Listening...</p>}{' '}
        </div>

        <Stack horizontal className={styles.chatInput}>
          {isLoading && (
            <Stack
              horizontal
              className={styles.stopGeneratingContainer}
              role="button"
              aria-label="Stop generating"
              tabIndex={0}
              onClick={stopGenerating}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? stopGenerating() : null)}
            >
              <SquareRegular className={styles.stopGeneratingIcon} aria-hidden="true" />
              <span className={styles.stopGeneratingText} aria-hidden="true">
                Stop generating
              </span>
            </Stack>
          )}
          <BroomRegular
            className={`${styles.clearChatBroom} ${styles.mobileclearChatBroom}`}
            style={{
              background:
                isLoading || answers.length === 0
                  ? '#BDBDBD'
                  : 'radial-gradient(109.81% 107.82% at 100.1% 90.19%, #0F6CBD 33.63%, #2D87C3 70.31%, #8DDDD8 100%)',
              cursor: isLoading || answers.length === 0 ? '' : 'pointer',
            }}
            onClick={clearChat}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? clearChat() : null)}
            aria-label="Clear session"
            role="button"
            tabIndex={0}
          />
          <QuestionInput
            clearOnSend
            placeholder="Type a new question..."
            disabled={isLoading}
            onSend={question => makeApiRequest(question)}
            recognizedText={recognizedText}
            onMicrophoneClick={onMicrophoneClick}
            onStopClick={stopSpeechRecognition}
            isListening={isListening}
            isRecognizing={isRecognizing}
            setRecognizedText={setRecognizedText}
          />
        </Stack>
      </div>
      {answers.length > 0 && isCitationPanelOpen && activeCitation && (
        <Stack.Item className={`${styles.citationPanel} ${styles.mobileStyles}`}>
          <Stack
            horizontal
            className={styles.citationPanelHeaderContainer}
            horizontalAlign="space-between"
            verticalAlign="center"
          >
            <span className={styles.citationPanelHeader}>Citations</span>
            <DismissRegular className={styles.citationPanelDismiss} onClick={() => setIsCitationPanelOpen(false)} />
          </Stack>
          <h5 className={`${styles.citationPanelTitle} ${styles.mobileCitationPanelTitle}`}>{activeCitation[2]}</h5>
          <ReactMarkdown
            className={`${styles.citationPanelContent} ${styles.mobileCitationPanelContent}`}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {activeCitation[0]}
          </ReactMarkdown>
        </Stack.Item>
      )}
    </>
  );
};

export default InterviewQuestions;
